/**
 * The instructions gate. The first thing a respondent sees, before the form.
 *
 * The body copy below was supplied and is rendered verbatim, including its
 * punctuation. Do not reword it.
 *
 * ONE CONFLICT, DELIBERATELY LEFT IN PLACE AND FLAGGED:
 * the last paragraph tells the respondent "you'll see a dashboard revealing
 * your workplace strengths". This app shows no result. Spec FR7 and section 5
 * put respondent-facing results out of scope, the completion screen says the
 * results are presented at the session, and FR10 keeps every read path behind
 * the admin token. So that sentence promises something the build does not do.
 * It is here because the copy was given as-is; it needs an owner's decision,
 * not a silent edit.
 *
 * The acknowledgement is a read-receipt for the instructions, not consent.
 * Consent to store name, email and responses is a separate, required checkbox
 * on the form that follows, and it is the one that writes participants
 * .consent_at. Do not merge the two.
 */

import { useId, useState } from 'react';

interface Props {
  /** Advances to the form. Nothing is created server side until that submits. */
  onAcknowledged: () => void;
}

export function Instructions({ onAcknowledged }: Props) {
  const ackId = useId();
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div className="card instructions">
      <h1>Instructions</h1>

      <p>Each question includes three statements. For each question, you’ll select two statements:</p>

      <ul>
        <li>One that describes how you most naturally operate.</li>
        <li>One that describes how you least naturally operate.</li>
      </ul>

      <p>Before you choose, you will leave one option out.</p>

      <p>
        Some questions may feel challenging; trust your instincts and don’t overthink it. These
        questions focus on “who you are” rather than “who you aspire to be.”
      </p>

      <p>
        A scientifically validated process will analyze your responses to show how people like you
        approach work, make decisions, and build relationships. At the end, you’ll see a dashboard
        revealing your workplace strengths.
      </p>

      {/* Same markup as the consent row on the form, so the tap target and
          spacing are identical on a phone. */}
      <div className="consent">
        <input
          id={ackId}
          name="acknowledged"
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <label htmlFor={ackId}>I have read and understood these instructions.</label>
      </div>

      <button
        className="btn btn-primary"
        type="button"
        disabled={!acknowledged}
        onClick={onAcknowledged}
      >
        Start Discovery
      </button>
    </div>
  );
}
